/**
 * Where a screen stands relative to the Clients area (015 `contracts/screens.md` § 020 Accounts and
 * pipeline, the block of 2026-09-27): inside the Clients rail mode or not, and scoped to one client
 * (`/{ws}/clients/{account}` and the routes under it) or not. The quick creates open with their
 * client context from it, and the task detail opened there leaves out what belongs to the
 * development side.
 */
export interface ClientScope {
  readonly clients: boolean;
  readonly accountId: string | null;
}

/**
 * The first path segments below `/{ws}/` the Clients mode owns (`modes.tsx`). They live here, with no
 * import of the mode registry, so a lazy screen that asks where it stands (a task list opening a task's
 * page, 2026-10-07) does not pull the registry's sidebars into its chunk, which split the shell's first
 * load into shared chunks and grew it by 2.3 KB gzipped.
 */
export const CLIENTS_PREFIXES: readonly string[] = ["clients", "client-groups", "client-tasks", "unassigned-emails", "library", "accounts", "account-groups", "client-items"];

/** The page under `/{ws}/clients/` that is no client's: the status report of every client (`routes.tsx`), which the router ranks above a client's address. */
const CLIENTS_PAGES: readonly string[] = ["status-report"];

export function clientScopeOf(ws: string, pathname: string): ClientScope {
  const base = `/${ws}/`;
  const clients = pathname.startsWith(base) && CLIENTS_PREFIXES.includes(pathname.slice(base.length).split("/")[0] ?? "");
  const account = new RegExp(`^/${ws}/clients/([^/]+)`).exec(pathname)?.[1];
  return { clients, accountId: account === undefined || CLIENTS_PAGES.includes(account) ? null : decodeURIComponent(account) };
}
